import React, { useState, useEffect, useCallback } from 'react';
import Drawer from '@mui/material/Drawer';
import { 
  Edit2, 
  Check, 
  MapPin, 
  Phone, 
  User, 
  ChevronLeft,
  ShieldCheck
} from 'lucide-react';

// Professional vector avatar
const AVATAR_URL = "https://avatar.iran.liara.run/public/girl?username=Jessica";

// Avatar Component
const Avatar = ({ url }) => {
  return (
    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border-4 border-white shadow-lg overflow-hidden">
      <img 
        src={url} 
        alt="Profile" 
        className="w-full h-full object-cover"
      />
    </div>
  );
};

// InputGroup Component
const InputGroup = ({ label, name, value, isEditing, type = 'text', onChange, icon }) => {
  const handleChange = (e) => {
    onChange(name, e.target.value);
  };

  return (
    <div className="space-y-1.5">
      {/* Label Row: Icon + Text */}
      <div className="flex items-center gap-2 ml-1">
        <div className="text-gray-400">
          {icon}
        </div>
        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">
          {label}
        </label>
      </div>

      <div className="relative">
        {type === 'textarea' ? (
          <textarea
            value={value}
            onChange={handleChange}
            disabled={!isEditing}
            rows={2}
            className={`
              w-full px-4 py-2.5 rounded-lg border transition-all duration-200 resize-none text-sm font-medium
              ${isEditing 
                ? 'bg-white border-gray-300 text-gray-900 focus:border-gray-900 focus:ring-1 focus:ring-gray-900' 
                : 'bg-gray-50 border-gray-200 text-gray-600'}
            `}
            placeholder={`Enter your ${label.toLowerCase()}...`}
          />
        ) : (
          <input
            type={type}
            value={value}
            onChange={handleChange}
            disabled={!isEditing}
            className={`
              w-full px-4 py-2.5 rounded-lg border transition-all duration-200 text-sm font-medium
              ${isEditing 
                ? 'bg-white border-gray-300 text-gray-900 focus:border-gray-900 focus:ring-1 focus:ring-gray-900' 
                : 'bg-gray-50 border-gray-200 text-gray-600'}
            `}
            placeholder={`Enter your ${label.toLowerCase()}...`}
          />
        )}
      </div>
    </div>
  );
};

const AccountDrawer = ({ open, onClose }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Initialize state
  const [profile, setProfile] = useState({
    id: '',
    name: '',
    address: '',
    phone: '',
    isGuest: true,
  });

  // Simulate fetching user data on mount
  useEffect(() => {
    const storedId = localStorage.getItem('userId');
    
    if (storedId) {
      // Simulate returning user
      setProfile({
        id: storedId,
        name: 'Ammar Azad Khan',
        address: '24/2 Lalbagh, Near Azimpur Model Primary School, Lalbagh, Dhaka',
        phone: '01608050373',
        isGuest: false
      });
    } else {
      // New Guest
      const guestId = `GST-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
      setProfile(prev => ({
        ...prev,
        id: guestId,
        isGuest: true,
      }));
    }
  }, []);

  const handleInputChange = useCallback((name, value) => {
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const toggleEdit = () => {
    if (isEditing) {
      handleSave();
    } else {
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setIsEditing(false);
      // If this was a guest, they are now "registered" if they added a name
      if (profile.isGuest && profile.name) {
        setProfile(prev => ({ ...prev, isGuest: false }));
        localStorage.setItem('userId', profile.id);
      }
    }, 800);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': { 
          width: { xs: '85%', sm: 380 },
          maxWidth: '380px',
          boxSizing: 'border-box',
          bgcolor: 'white',
          maxHeight: '100dvh'
        }
      }}
    >
      <div className="w-full bg-white flex flex-col h-dvh">
        
        {/* Header / Nav - Compact */}
        <header className="flex justify-between items-center px-5 py-4 bg-white border-b border-gray-100 flex-shrink-0">
          <button 
            onClick={onClose}
            className="p-2 rounded-full bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors border border-gray-100 shadow-sm cursor-pointer"
          >
            <ChevronLeft size={18} />
          </button>
          <h1 className="text-xs font-bold tracking-[0.2em] uppercase text-gray-400">
            Profile
          </h1>
          <div className="w-8"></div>
        </header>

        {/* Content Scroll Area - Compact */}
        <div className="px-5 pt-2 pb-2 flex-1 overflow-y-auto">
          
          {/* Avatar & ID - Compact */}
          <div className="flex flex-col items-center mb-6">
            <Avatar url={AVATAR_URL} />
            
            <div className="mt-2 text-center space-y-1.5">
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                {profile.name || 'Guest Visitor'}
              </h2>
              <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-50 border border-gray-100 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></span>
                <span className="text-[9px] font-bold text-gray-400 tracking-wider font-mono uppercase">
                  ID: {profile.id}
                </span>
              </div>
            </div>
          </div>

          {/* Form Fields - Compact */}
          <div className="space-y-4">
            <InputGroup 
              label="Full Name"
              name="name"
              value={profile.name}
              isEditing={isEditing}
              onChange={handleInputChange}
              icon={<User size={14} />}
            />

            <InputGroup 
              label="Mobile Number"
              name="phone"
              value={profile.phone}
              isEditing={isEditing}
              type="tel"
              onChange={handleInputChange}
              icon={<Phone size={14} />}
            />

            <InputGroup 
              label="Delivery Address"
              name="address"
              value={profile.address}
              isEditing={isEditing}
              type="textarea"
              onChange={handleInputChange}
              icon={<MapPin size={14} />}
            />
          </div>
          
        </div>

        {/* Footer - Compact */}
        <div className="px-5 py-2 bg-white border-t border-gray-100 flex-shrink-0">
          <button
            onClick={toggleEdit}
            disabled={isLoading}
            className={`
              w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-xs uppercase tracking-widest transition-all duration-300 shadow mb-2 cursor-pointer
              ${isEditing 
                ? 'bg-gray-900 text-white hover:bg-black' 
                : 'bg-gray-900 text-white hover:bg-black'}
            `}
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : isEditing ? (
              <>
                <Check size={16} />
                Save Changes
              </>
            ) : (
              <>
                <Edit2 size={16} />
                Edit Profile
              </>
            )}
          </button>

          {/* Info Card Box - Compact */}
          <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
            {/* Fixed alignment here using items-center */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center flex-shrink-0">
                <ShieldCheck size={12} className="text-gray-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-[9px] font-bold text-gray-900 uppercase tracking-widest mb-0.5">Secure & Convenient</h3>
                <p className="text-[9px] leading-relaxed text-gray-500 font-medium">
                  We only use these details to automatically fill your shipping information when you place an order.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default AccountDrawer;